import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  Button,
  Card,
  Modal,
  PageHeader,
  Pill,
  Table,
  Td,
  Th,
} from "@/components/ui";
import { confirmPopup } from "@/components/popup";

interface ClusterServerSummary {
  id: number;
  name: string;
  host: string;
  port: number;
  is_active: boolean;
  connections: number | null;
  reachable: boolean | null;
  active: boolean | null;
}

interface Cluster {
  id: number;
  name: string;
  domain: string;
  public_secret: string;
  ad_tag: string | null;
  port: number;
  is_active: boolean;
  created_at: string;
  servers: ClusterServerSummary[];
  total_connections: number;
  link: string;
}

interface ServerRow {
  id: number;
  name: string;
  host: string;
  server_type: string;
  is_active: boolean;
  cluster_id: number | null;
}

interface SyncResult {
  total: number;
  ok: number;
  errors: { server_id: number; error: string }[];
}

export default function ClusterDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const clusterId = Number(id);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [attachOpen, setAttachOpen] = useState(false);

  const q = useQuery<Cluster>({
    queryKey: ["cluster", clusterId],
    queryFn: () => api<Cluster>(`/api/admin/clusters/${clusterId}`),
    enabled: !Number.isNaN(clusterId),
    refetchInterval: 15000,
  });

  const regenSecret = useMutation<Cluster>({
    mutationFn: () =>
      api<Cluster>(`/api/admin/clusters/${clusterId}`, {
        method: "PATCH",
        body: JSON.stringify({ public_secret: randomSecret() }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cluster", clusterId] }),
  });

  const syncAll = useMutation<SyncResult>({
    mutationFn: () =>
      api<SyncResult>(`/api/admin/clusters/${clusterId}/sync-all`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cluster", clusterId] });
      qc.invalidateQueries({ queryKey: ["servers"] });
    },
  });

  const detach = useMutation({
    mutationFn: (serverId: number) =>
      api(`/api/admin/clusters/${clusterId}/detach/${serverId}`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cluster", clusterId] });
      qc.invalidateQueries({ queryKey: ["clusters"] });
      qc.invalidateQueries({ queryKey: ["servers"] });
    },
  });

  const remove = useMutation({
    mutationFn: () =>
      api(`/api/admin/clusters/${clusterId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clusters"] });
      qc.invalidateQueries({ queryKey: ["servers"] });
      navigate("/clusters");
    },
  });

  if (q.isLoading) return <div className="p-4 md:p-8 text-zinc-400">Загрузка…</div>;
  if (q.isError || !q.data)
    return <div className="p-4 md:p-8 text-red-400">Не нашли кластер</div>;

  const c = q.data;

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title={`Кластер — ${c.name}`}
        right={
          <Link to="/clusters" className="text-sm text-zinc-400 hover:text-zinc-200">
            ← к списку
          </Link>
        }
      />

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card title="Основное">
          <dl className="text-sm space-y-2.5">
            <Row k="домен" v={<span className="font-mono">{c.domain}</span>} />
            <Row k="порт" v={c.port} />
            <Row
              k="ad_tag"
              v={
                <AdTagEdit
                  clusterId={c.id}
                  value={c.ad_tag}
                  onSaved={() =>
                    qc.invalidateQueries({ queryKey: ["cluster", c.id] })
                  }
                />
              }
            />
            <Row
              k="статус"
              v={
                c.is_active ? (
                  <Pill tone="success">активен</Pill>
                ) : (
                  <Pill tone="danger">выключен</Pill>
                )
              }
            />
          </dl>
          <p className="text-xs text-zinc-500 mt-3">
            Если тег пуст — применится глобальный из «Настройки → MTProxy». После
            смены тега нажми «Синхронизировать все» чтобы серверы подхватили.
          </p>
        </Card>

        <Card title="Нагрузка">
          <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">
            Суммарно
          </div>
          <div className="text-4xl font-semibold text-emerald-300">
            {c.total_connections.toLocaleString("ru-RU")}
          </div>
          <div className="text-xs text-zinc-500 mt-1">активных коннектов</div>
          <div className="text-xs text-zinc-500 mt-3">
            серверов: {c.servers.filter((s) => s.reachable && s.active).length}/
            {c.servers.length} живых
          </div>
        </Card>

        <Card title="Опасная зона">
          <div className="text-sm text-zinc-400 mb-3">
            Удаление кластера безвозвратно. Серверы не удаляются — просто отвязываются
            (остаются в списке Серверы как standalone).
          </div>
          <Button
            variant="danger"
            disabled={remove.isPending}
            onClick={async () => {
              if (
                await confirmPopup({
                  title: "Удалить кластер?",
                  body: `Кластер «${c.name}». ${c.servers.length} серверов отвяжутся и останутся standalone.`,
                  confirmLabel: "Удалить",
                  danger: true,
                })
              ) {
                remove.mutate();
              }
            }}
          >
            Удалить кластер
          </Button>
        </Card>
      </section>

      <section className="mb-6">
        <Card title="Публичная рекламная ссылка">
          <div className="flex gap-3 items-stretch">
            <div className="flex-1 px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-800 font-mono text-xs text-zinc-100 break-all">
              {c.link}
            </div>
            <CopyButton text={c.link} />
          </div>
          <p className="text-xs text-zinc-500 mt-3">
            Эта ссылка раздаётся в рекламу. DNS-резолвер клиента попадёт на один из
            серверов кластера (round-robin по A-записям {c.domain} в Cloudflare).
            После смены secret'а нажми «Синхронизировать все» чтобы docker на всех
            серверах поднялся с новым значением.
          </p>
          <div className="mt-4 flex gap-3 flex-wrap">
            <Button
              variant="ghost"
              disabled={regenSecret.isPending}
              onClick={async () => {
                if (
                  await confirmPopup({
                    title: "Сгенерировать новый public_secret кластера?",
                    body: "Старая ссылка перестанет работать у всех кто её сохранил. Серверы обновятся после Sync.",
                    confirmLabel: "Сгенерировать",
                    danger: true,
                  })
                ) {
                  regenSecret.mutate();
                }
              }}
            >
              {regenSecret.isPending ? "Генерируем…" : "Пересоздать secret"}
            </Button>
            <Button
              variant="primary"
              disabled={syncAll.isPending || c.servers.length === 0}
              onClick={async () => {
                if (
                  await confirmPopup({
                    title: "Синхронизировать все серверы?",
                    body: `Переустановить mtproto-proxy на всех ${c.servers.length} серверах кластера? 1-2 мин на сервер, идёт последовательно.`,
                    confirmLabel: "Синхронизировать",
                  })
                ) {
                  syncAll.mutate();
                }
              }}
            >
              {syncAll.isPending
                ? "Синхронизируем…"
                : `Синхронизировать все (${c.servers.length})`}
            </Button>
          </div>
          {syncAll.data && (
            <div className="mt-3 text-xs">
              {syncAll.data.ok === syncAll.data.total ? (
                <span className="text-emerald-400">
                  OK: {syncAll.data.ok}/{syncAll.data.total} серверов обновлены.
                </span>
              ) : (
                <>
                  <div className="text-amber-400">
                    Готово: {syncAll.data.ok}/{syncAll.data.total}. Ошибки:
                  </div>
                  <ul className="text-red-400 mt-1 space-y-0.5">
                    {syncAll.data.errors.map((e, i) => (
                      <li key={i}>
                        #{e.server_id}: {e.error}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </Card>
      </section>

      <section className="mb-6">
        <Card title="Регистрация в @MTProxyAdminBot">
          <ol className="text-sm text-zinc-300 space-y-3 list-decimal list-inside">
            <li>
              Открой{" "}
              <a
                href="https://t.me/MTProxybot"
                target="_blank"
                rel="noreferrer"
                className="text-brand hover:underline"
              >
                @MTProxybot
              </a>{" "}
              и жми /newproxy.
            </li>
            <li>
              На «Send me its address in format host:port» отправь:
              <div className="mt-2">
                <CopyRow text={`${c.domain}:${c.port}`} />
              </div>
            </li>
            <li>
              На «Now please specify its secret» отправь <b>чистые 32 hex</b> —{" "}
              <span className="text-amber-300">без префикса dd/ee</span>:
              <div className="mt-2">
                <CopyRow text={c.public_secret} mono />
              </div>
            </li>
            <li>
              Бот вернёт тег и тестовую ссылку. Тег (32 hex) вставь в поле{" "}
              <code className="bg-zinc-900 px-1.5 py-0.5 rounded">ad_tag</code>{" "}
              выше (в «Основное»), сохрани и жми «Синхронизировать все».
            </li>
          </ol>
          <p className="text-xs text-zinc-500 mt-4">
            `dd` в пользовательской ссылке — это клиентский префикс FakeTLS, не часть
            секрета. @MTProxybot хочет только 32 hex, наша генерация уже выдаёт
            именно такой формат (raw выше).
          </p>
        </Card>
      </section>

      <section className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm uppercase tracking-wider text-zinc-400">
            Серверы в кластере ({c.servers.length})
          </h2>
          <Button variant="primary" onClick={() => setAttachOpen(true)}>
            + Добавить сервер
          </Button>
        </div>
        {c.servers.length === 0 ? (
          <div className="text-zinc-500 text-sm">
            Пока нет серверов. Добавь существующий ad-сервер через «+ Добавить сервер»
            или создай новый в разделе Серверы.
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>id</Th>
                <Th>имя</Th>
                <Th>host</Th>
                <Th>сервис</Th>
                <Th>коннекты</Th>
                <Th align="right" fit>
                  действия
                </Th>
              </tr>
            </thead>
            <tbody>
              {c.servers.map((s) => (
                <tr key={s.id} className="hover:bg-zinc-900/50">
                  <Td className="text-zinc-500">
                    <Link
                      to={`/servers/${s.id}`}
                      className="text-brand hover:underline"
                    >
                      #{s.id}
                    </Link>
                  </Td>
                  <Td className="text-zinc-100">{s.name}</Td>
                  <Td className="font-mono text-zinc-300">{s.host}</Td>
                  <Td>
                    {s.reachable === null ? (
                      <span className="text-zinc-600">—</span>
                    ) : s.reachable && s.active ? (
                      <Pill tone="success">active</Pill>
                    ) : (
                      <Pill tone="danger">down</Pill>
                    )}
                  </Td>
                  <Td className="font-mono text-emerald-300">
                    {(s.connections ?? 0).toLocaleString("ru-RU")}
                  </Td>
                  <Td align="right" fit>
                    <div className="flex justify-end gap-3">
                      <Link
                        to={`/servers/${s.id}`}
                        className="inline-flex items-center h-8 px-3 text-xs font-medium rounded-md border bg-zinc-900 text-zinc-300 border-zinc-800 hover:bg-zinc-700 hover:text-white hover:border-zinc-600 transition-colors"
                      >
                        Открыть
                      </Link>
                      <Button
                        variant="danger"
                        disabled={detach.isPending}
                        onClick={async () => {
                          if (
                            await confirmPopup({
                              title: `Вывести сервер #${s.id} из кластера?`,
                              confirmLabel: "Отвязать",
                              danger: true,
                            })
                          ) {
                            detach.mutate(s.id);
                          }
                        }}
                      >
                        Отвязать
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </section>

      <Modal
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        title="Привязать сервер к кластеру"
      >
        <AttachServer
          clusterId={c.id}
          onAttached={() => {
            setAttachOpen(false);
            qc.invalidateQueries({ queryKey: ["cluster", c.id] });
            qc.invalidateQueries({ queryKey: ["clusters"] });
          }}
          onCancel={() => setAttachOpen(false)}
        />
      </Modal>
    </div>
  );
}

function AttachServer({
  clusterId,
  onAttached,
  onCancel,
}: {
  clusterId: number;
  onAttached: () => void;
  onCancel: () => void;
}) {
  const navigate = useNavigate();
  const q = useQuery<{ servers: ServerRow[] }>({
    queryKey: ["servers"],
    queryFn: () => api("/api/admin/servers"),
  });

  const attach = useMutation({
    mutationFn: (serverId: number) =>
      api(`/api/admin/clusters/${clusterId}/attach/${serverId}`, { method: "POST" }),
    onSuccess: () => onAttached(),
  });

  const candidates = (q.data?.servers ?? []).filter(
    (s) => s.server_type === "ad" && s.cluster_id == null,
  );

  return (
    <div className="space-y-4">
      {q.isLoading && <div className="text-zinc-400 text-sm">Загружаем…</div>}
      {!q.isLoading && candidates.length === 0 && (
        <div className="text-sm text-zinc-400">
          Нет ad-серверов без кластера. Создай новый сервер (тип <code>ad</code>) в
          разделе «Серверы», потом вернись сюда.
          <div className="mt-3">
            <Button variant="primary" onClick={() => navigate("/servers")}>
              К Серверам →
            </Button>
          </div>
        </div>
      )}
      {candidates.length > 0 && (
        <>
          <div className="text-sm text-zinc-400">
            Выбери существующий ad-сервер. Его <code>public_secret</code> и
            <code> ad_tag</code> будут перезаписаны значениями кластера при следующем
            Install.
          </div>
          <div className="border border-zinc-800 rounded-lg divide-y divide-zinc-800 max-h-80 overflow-auto">
            {candidates.map((s) => (
              <button
                key={s.id}
                onClick={() => attach.mutate(s.id)}
                disabled={attach.isPending}
                className="w-full px-4 py-3 text-left hover:bg-zinc-800/40 transition-colors disabled:opacity-50"
              >
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-zinc-100 text-sm">
                      #{s.id} {s.name}
                    </div>
                    <div className="font-mono text-xs text-zinc-500">{s.host}</div>
                  </div>
                  <div className="text-xs text-zinc-400">
                    {s.is_active ? "активен" : "выключен"}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
      {attach.isError && (
        <div className="text-xs text-red-400">{(attach.error as Error).message}</div>
      )}
      <div className="flex justify-end pt-2">
        <Button variant="ghost" onClick={onCancel} disabled={attach.isPending}>
          Отмена
        </Button>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-6">
      <dt className="text-zinc-500 shrink-0">{k}</dt>
      <dd className="text-zinc-100 text-right">{v}</dd>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="ghost"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* ignore */
        }
      }}
    >
      {copied ? "Скопировано ✓" : "Копировать"}
    </Button>
  );
}

function randomSecret(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

function CopyRow({ text, mono = false }: { text: string; mono?: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex gap-2 items-stretch">
      <div
        className={`flex-1 px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-800 text-xs text-zinc-100 break-all ${
          mono ? "font-mono" : ""
        }`}
      >
        {text}
      </div>
      <button
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            /* ignore */
          }
        }}
        className="h-auto px-3 text-xs font-medium rounded-md border bg-zinc-900 text-zinc-200 border-zinc-800 hover:bg-zinc-700 hover:text-white hover:border-zinc-600 transition-colors"
      >
        {copied ? "✓" : "Копир."}
      </button>
    </div>
  );
}

function AdTagEdit({
  clusterId,
  value,
  onSaved,
}: {
  clusterId: number;
  value: string | null;
  onSaved: () => void;
}) {
  const initial = value ?? "";
  const [input, setInput] = useState<string>(initial);

  useEffect(() => {
    setInput(value ?? "");
  }, [value]);

  const trimmed = input.trim().toLowerCase();
  const invalid =
    trimmed.length > 0 && (trimmed.length !== 32 || !/^[0-9a-f]+$/.test(trimmed));
  const dirty = trimmed !== initial;

  const save = useMutation({
    mutationFn: () =>
      api(`/api/admin/clusters/${clusterId}`, {
        method: "PATCH",
        body: JSON.stringify({ ad_tag: trimmed || null }),
      }),
    onSuccess: () => onSaved(),
  });

  return (
    <div className="inline-flex items-center gap-2 flex-wrap justify-end">
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && dirty && !invalid) save.mutate();
          if (e.key === "Escape") setInput(initial);
        }}
        placeholder="пусто = глобал"
        className="h-7 w-48 px-2 rounded-md bg-zinc-950 border border-zinc-800 text-xs font-mono text-zinc-100 text-right focus:outline-none focus:border-brand placeholder:text-zinc-600"
      />
      {dirty && (
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending || invalid}
          className="h-7 px-3 text-xs rounded-md bg-brand/15 text-brand border border-brand/40 hover:bg-brand hover:text-brand-fg hover:border-brand disabled:opacity-40"
        >
          {save.isPending ? "…" : "Сохранить"}
        </button>
      )}
      {dirty && (
        <button
          onClick={() => setInput(initial)}
          disabled={save.isPending}
          className="h-7 px-2 text-xs rounded-md bg-zinc-900 text-zinc-400 border border-zinc-800 hover:bg-zinc-700 hover:text-white disabled:opacity-40"
        >
          ✕
        </button>
      )}
      {invalid && <span className="text-xs text-red-400">32 hex</span>}
      {save.isError && (
        <span className="text-xs text-red-400">{(save.error as Error).message}</span>
      )}
    </div>
  );
}
